import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  forwardRef
} from '@angular/core';

import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR
} from '@angular/forms';

import { SelectionModel } from '@angular/cdk/collections';
import { NestedTreeControl } from '@angular/cdk/tree';
import { MatTreeNestedDataSource } from '@angular/material/tree';


export interface BranchNode {
  id: number | null;
  name: string;
  operatorId?: number;
  storeType?: string;
  children?: BranchNode[];
}


@Component({
  selector: 'app-branches-tree',
  standalone: false,
  templateUrl: './branches-tree.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BranchesTreeComponent),
      multi: true
    }
  ]
})
export class BranchesTreeComponent
  implements OnInit, OnChanges, ControlValueAccessor {


  // =========================================================
  // Inputs
  // =========================================================

  @Input() data: BranchNode[] = [];

  @Input() electronicBranch: BranchNode | null = null;


  // =========================================================
  // Tree
  // =========================================================

  treeControl = new NestedTreeControl<BranchNode>(
    node => node.children
  );

  dataSource = new MatTreeNestedDataSource<BranchNode>();


  // =========================================================
  // Selection
  // =========================================================

  checklistSelection =
    new SelectionModel<BranchNode>(true);


  // =========================================================
  // State
  // =========================================================

  isDisabled = false;

  searchQuery = '';


  /**
   * Important:
   *
   * Angular can call writeValue() before the API
   * finishes loading the tree data.
   *
   * So we keep the value until data is available.
   */
  private pendingValue: number[] | null = null;


  // =========================================================
  // ControlValueAccessor
  // =========================================================

  private onChange:
    (value: number[]) => void = () => { };

  private onTouched:
    () => void = () => { };


  // =========================================================
  // Lifecycle
  // =========================================================

  ngOnInit(): void {
    this.updateTreeData();
  }


  ngOnChanges(changes: SimpleChanges): void {

    if (
      changes['data'] ||
      changes['electronicBranch']
    ) {
      this.updateTreeData();
    }
  }


  private updateTreeData(): void {

    this.dataSource.data = this.data || [];

    /**
     * If writeValue() was called before API data arrived,
     * restore the selection now.
     */
    if (this.pendingValue) {
      this.applySelectedValue(this.pendingValue);
    }
  }


  // =========================================================
  // Tree
  // =========================================================

  hasChild = (
    _level: number,
    node: BranchNode
  ): boolean => {

    return !!node.children?.length;
  };


  private isLeaf(
    node: BranchNode
  ): boolean {

    return !node.children?.length;
  }


  // =========================================================
  // ControlValueAccessor
  // =========================================================

  writeValue(
    value: number[] | null
  ): void {

    this.pendingValue = value;

    this.checklistSelection.clear();

    /**
     * API hasn't loaded yet.
     */
    if (
      !this.dataSource.data.length &&
      !this.electronicBranch
    ) {
      return;
    }

    this.applySelectedValue(value);
  }


  private applySelectedValue(
    selectedIds: number[] | null
  ): void {

    this.checklistSelection.clear();

    if (!selectedIds?.length) {
      return;
    }

    const allSelectableNodes =
      this.getAllSelectableBranches();

    selectedIds.forEach(id => {

      const node =
        allSelectableNodes.find(item =>
          item.id === null
            ? id === -1
            : item.id === id
        );

      if (node) {
        this.checklistSelection.select(node);
      }

    });
  }


  registerOnChange(
    fn: (value: number[]) => void
  ): void {

    this.onChange = fn;
  }

  registerOnTouched(
    fn: () => void
  ): void {

    this.onTouched = fn;
  }


  setDisabledState(
    isDisabled: boolean
  ): void {

    this.isDisabled = isDisabled;
  }


  // =========================================================
  // Selection
  // =========================================================

  isNodeSelected(
    node: BranchNode
  ): boolean {

    return this.checklistSelection.isSelected(node);
  }


  // =========================================================
  // Parent selection
  // =========================================================

  isParentSelected(
    node: BranchNode
  ): boolean {

    const descendants =
      this.getLeafNodes(
        node.children || []
      );


    return (
      descendants.length > 0 &&
      descendants.every(child =>
        this.checklistSelection.isSelected(child)
      )
    );
  }


  isParentIndeterminate(
    node: BranchNode
  ): boolean {

    const descendants =
      this.getLeafNodes(
        node.children || []
      );


    if (!descendants.length) {
      return false;
    }


    const selectedCount =
      descendants.filter(child =>
        this.checklistSelection.isSelected(child)
      ).length;


    return (
      selectedCount > 0 &&
      selectedCount < descendants.length
    );
  }


  toggleNodeSelection(
    node: BranchNode
  ): void {

    const descendants =
      this.getLeafNodes(
        node.children || []
      );


    const shouldSelect =
      !this.isParentSelected(node);


    descendants.forEach(child => {

      if (shouldSelect) {

        this.checklistSelection.select(child);

      } else {

        this.checklistSelection.deselect(child);

      }

    });


    this.propagateChange();
  }


  // =========================================================
  // Leaf selection
  // =========================================================

  toggleLeafSelection(
    node: BranchNode
  ): void {

    if (this.isDisabled) {
      return;
    }


    this.checklistSelection.toggle(node);

    this.propagateChange();
  }


  // =========================================================
  // Electronic branch
  // =========================================================

  isElectronicBranchSelected(): boolean {

    if (!this.electronicBranch) {
      return false;
    }


    return this.checklistSelection.isSelected(
      this.electronicBranch
    );
  }


  toggleElectronicBranch(): void {

    if (
      this.isDisabled ||
      !this.electronicBranch
    ) {
      return;
    }


    this.checklistSelection.toggle(
      this.electronicBranch
    );


    this.propagateChange();
  }


  // =========================================================
  // Select All
  // =========================================================

  private getAllSelectableBranches(): BranchNode[] {

    const branches =
      this.getLeafNodes(
        this.dataSource.data
      );


    /**
     * Electronic branch is separate from the city tree.
     */
    if (this.electronicBranch) {

      branches.push(
        this.electronicBranch
      );
    }


    return branches;
  }


  isAllSelected(): boolean {

    const branches =
      this.getAllSelectableBranches();


    return (
      branches.length > 0 &&
      branches.every(branch =>
        this.checklistSelection.isSelected(branch)
      )
    );
  }


  isMasterIndeterminate(): boolean {

    const branches =
      this.getAllSelectableBranches();


    const selectedCount =
      branches.filter(branch =>
        this.checklistSelection.isSelected(branch)
      ).length;


    return (
      selectedCount > 0 &&
      selectedCount < branches.length
    );
  }


  masterToggle(): void {

    if (this.isDisabled) {
      return;
    }


    const branches =
      this.getAllSelectableBranches();


    if (this.isAllSelected()) {

      branches.forEach(branch =>
        this.checklistSelection.deselect(branch)
      );

    } else {

      branches.forEach(branch =>
        this.checklistSelection.select(branch)
      );

    }


    this.propagateChange();
  }


  // =========================================================
  // Propagate form value
  // =========================================================

  private propagateChange(): void {

    const selectedIds: number[] =

      this.checklistSelection.selected

        .filter(node =>
          this.isLeaf(node)
        )

        .map(node =>
          node.id === null ? -1 : node.id as number
        );


    this.onChange(
      selectedIds
    );

    this.onTouched();
  }

  // =========================================================
  // Get leaf nodes
  // =========================================================

  private getLeafNodes(
    nodes: BranchNode[]
  ): BranchNode[] {

    let result: BranchNode[] = [];


    nodes.forEach(node => {

      if (this.isLeaf(node)) {

        result.push(node);

      } else {

        result =
          result.concat(
            this.getLeafNodes(
              node.children!
            )
          );

      }

    });


    return result;
  }


  // =========================================================
  // Get all nodes
  // =========================================================

  private getAllNodes(
    nodes: BranchNode[]
  ): BranchNode[] {

    let result: BranchNode[] = [];


    nodes.forEach(node => {

      result.push(node);


      if (node.children?.length) {

        result =
          result.concat(
            this.getAllNodes(
              node.children
            )
          );

      }

    });


    return result;
  }


  // =========================================================
  // Search
  // =========================================================

  filterBranches(
    event: Event
  ): void {

    const filterValue =
      (
        event.target as HTMLInputElement
      )
        .value
        .trim()
        .toLowerCase();


    this.searchQuery =
      filterValue;


    /**
     * Show original data.
     */
    if (!filterValue) {

      this.dataSource.data =
        this.data;

      return;
    }


    const filteredData =
      this.data

        .map(group => {

          const matchingChildren =
            group.children?.filter(child =>
              child.name
                .toLowerCase()
                .includes(filterValue)
            );


          const groupMatches =
            group.name
              .toLowerCase()
              .includes(filterValue);


          if (
            groupMatches ||
            (
              matchingChildren &&
              matchingChildren.length > 0
            )
          ) {

            return {

              ...group,

              children:
                matchingChildren?.length
                  ? matchingChildren
                  : group.children

            };

          }


          return null;

        })

        .filter(
          group => group !== null
        ) as BranchNode[];


    this.dataSource.data =
      filteredData;


    this.treeControl.expandAll();
  }
}
